import React from 'react';
import { Aperture, ArrowLeft } from 'lucide-react';
import { GALLERY_IMAGES } from '../constants';

interface GalleryProps {
  onNavigate: (page: string) => void;
}

const Gallery: React.FC<GalleryProps> = ({ onNavigate }) => {
  const auroraStyle: React.CSSProperties = {
    backgroundImage: 'linear-gradient(to right, hsla(228, 51%, 70%, 1) 0%, hsla(27, 90%, 68%, 1) 45%, hsla(29, 100%, 49%, 1) 50%, hsla(27, 90%, 68%, 1) 55%, hsla(228, 51%, 70%, 1) 100%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
  };

  return (
    <section className="px-4 md:px-6 pt-0 pb-12 md:py-20 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => onNavigate('home')}
          className="mb-8 flex items-center gap-2 text-slate-500 hover:text-primary transition-colors font-mono text-[10px] uppercase tracking-[0.2em] group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>
        
        <div className="flex flex-col md:flex-row items-end justify-between mb-8 border-b border-slate-200 dark:border-slate-800 pb-6">
          <div>
            <h1 
              className="text-4xl md:text-6xl font-bold uppercase font-display tracking-tighter mb-2 animate-aurora"
              style={auroraStyle}
            >
              Visual Logs
            </h1>
            <p className="font-mono text-primary text-xs tracking-[0.2em] uppercase">
              // Archive
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {GALLERY_IMAGES.map((img) => (
            // Card Container - Removed max-w constraints to fit space on mobile
            <div key={img.id} className="group relative w-full mx-auto aspect-[4/5] [perspective:1500px] hover:z-50">
              
              {/* INSIDE CONTENT (BASE LAYER) - The Full Image */}
              <div className="absolute inset-0 w-full h-full bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden flex flex-col">
                 <div className="relative h-full w-full overflow-hidden">
                    <img 
                        src={img.url} 
                        alt={img.title} 
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    />
                 </div>
              </div>

              {/* THE DOOR (FLIPPER) */}
              <div className="absolute inset-0 w-full h-full transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] origin-left [transform-style:preserve-3d] group-hover:[transform:rotateY(-135deg)] shadow-[10px_0_20px_rgba(0,0,0,0.5)]">
                  
                  {/* FRONT FACE (COVER) */}
                  <div className="absolute inset-0 [backface-visibility:hidden] bg-slate-950 border border-slate-800 flex flex-col overflow-hidden">
                      {/* Stylized Cover Image */}
                      <div className="relative flex-grow overflow-hidden bg-slate-900">
                          <img 
                            src={img.url} 
                            alt="Cover" 
                            className="w-full h-full object-cover opacity-60 grayscale contrast-125 group-hover:opacity-100 transition-opacity"
                          />
                          {/* Tech Grid Overlay */}
                          <div className="absolute inset-0 bg-[linear-gradient(rgba(18,18,18,0)_2px,transparent_2px),linear-gradient(90deg,rgba(18,18,18,0)_2px,transparent_2px)] bg-[size:40px_40px] [background-position:center] border border-white/5 opacity-50"></div>
                          
                          {/* Exclusive Viewing Label */}
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 border border-primary/50 text-primary/80 font-bold font-mono text-sm px-3 py-1 uppercase tracking-widest backdrop-blur-sm shadow-[0_0_15px_rgba(249,115,22,0.3)] whitespace-nowrap bg-black/40">
                              Exclusive Viewing
                          </div>
                      </div>
                      
                      {/* Footer of Cover */}
                      <div className="h-16 bg-slate-900 border-t border-slate-800 flex items-center justify-between px-4">
                          <div className="flex items-center gap-2">
                             <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                             <span className="font-mono text-[10px] text-slate-400">LOG #{img.id}092</span>
                          </div>
                          <Aperture className="text-slate-600" size={16} />
                      </div>
                  </div>

                  {/* BACK FACE (INSIDE OF DOOR) */}
                  <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] bg-slate-900 border-r border-slate-700 flex flex-col p-6 shadow-inner">
                      {/* Content removed as requested */}
                      <div className="flex-grow flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full border border-slate-800 flex items-center justify-center opacity-20">
                              <Aperture size={32} className="text-slate-500" />
                          </div>
                      </div>

                      <div className="mt-auto pt-4 border-t border-slate-700">
                          <p className="text-[9px] text-slate-600 leading-tight">
                              WARNING: This physical media contains proprietary tour data. Unauthorized distribution is prohibited by Starroad Touring Systems.
                          </p>
                      </div>
                  </div>

              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Gallery;